import { PayloadAction } from '@reduxjs/toolkit';

import { createAppSlice } from '@exp/redux/createAppSlice';

import type { PageHandle } from '../types/BaseFlow';
import { sanitizeObject } from '../utilities/utilities';

export type HistoryEntry = {
  name: string;
  backable?: boolean;
  className?: string;
  closable?: boolean;
  parent?: { name: string; className?: string; previousPage: PageHandle | null | undefined };
  previousPage?: PageHandle | null;
  replayable?: boolean;
  skippable?: boolean;
  tallPage?: boolean;
};

type HistoryState = HistoryEntry[];

const historySlice = createAppSlice({
  initialState: [] as HistoryState,
  name: 'history',
  reducers: (create) => ({
    pushHistory: create.preparedReducer(
      (page: Partial<HistoryEntry>) => ({ payload: sanitizeObject(page) as HistoryEntry }),
      (state, action: PayloadAction<HistoryEntry>) => {
        const existingIndex = state.findIndex((p) => p.name === action.payload.name);
        if (existingIndex !== -1) {
          // TODO find a way for pages to exist multiple times; maybe some flagged as transient?
          // The page already exists. Chop the history down to where it previously existed.
          return state.slice(existingIndex);
        }
        // The page does not already exist. Add it to the beginning of the stack.
        return [action.payload].concat(state);
      },
    ),
    removeHistory: create.preparedReducer(
      (page: Partial<HistoryEntry>) => ({ payload: sanitizeObject(page) as HistoryEntry }),
      (state, action: PayloadAction<HistoryEntry>) => {
        const [last] = state;
        if (last?.name === action.payload?.name) {
          state.splice(0, 1);
        }
      },
    ),
    replaceHistory: create.preparedReducer(
      (page: Partial<HistoryEntry> & Record<string, unknown>) => ({ payload: sanitizeObject(page) as HistoryEntry }),
      (state, action: PayloadAction<HistoryEntry>) =>
        state.map((p) => (p.name === action.payload?.name ? { ...p, ...action.payload } : p)),
    ),
  }),
  selectors: {
    selectHistory: (state: HistoryState): HistoryState => state,
    selectLastPage: (state: HistoryState): HistoryEntry | undefined => {
      const [last] = historySlice.getSelectors().selectHistory(state);
      return last;
    },
  },
});

export const { pushHistory, removeHistory, replaceHistory } = historySlice.actions;
export const { selectHistory, selectLastPage } = historySlice.selectors;

export default historySlice;
